import React, { Suspense } from 'react';
import { observer } from 'mobx-react-lite';
import {
  GlobalBaseTheme,
  Helmet,
  Link,
  LinkVariants,
  FullLightLogo,
  FullMediumLogo,
  useTheme,
} from '@groma-dev/blueprint';
import { FormattedMessage, useIntl } from 'react-intl';
import { AppConfig } from '../../../core/AppConfig';
import { AppRoutes } from '../../../core/routes';
import { ComponentFactory } from '../../ComponentFactory';
import { buildUrlWithParams, parseLocationSearch } from '../../utils/WebHelper';
import { getInvitationsStyles } from './styles/Invitations.styles';

const AcceptInvitation = React.lazy(() => ComponentFactory.AcceptInvitationAsync);

export enum InvitationMode {
  ACCEPT = 'accept',
  INVITE = 'invite',
}

export interface InvitationsProps {
  mode?: InvitationMode;
}

const Invitations = observer((props: InvitationsProps) => {
  const { mode = InvitationMode.INVITE } = props;
  const theme = useTheme();
  const styles = getInvitationsStyles();
  const isDarkTheme = theme.__base === GlobalBaseTheme.DARK;
  const { Seo } = AppConfig.Settings;
  const { token, email } = parseLocationSearch(location.search);
  const shouldShowLoading = mode === InvitationMode.ACCEPT && (!token || !email);

  const seoContent = () => {
    const { formatMessage } = useIntl();

    const titleContent = formatMessage({
      id: 'helmet.invitations.title',
      defaultMessage: 'Invitations | Groma',
      description: 'Invitations page title',
    });

    return Seo.enabled ? (
      <Helmet>
        <title>{titleContent}</title>
      </Helmet>
    ) : null;
  };

  const content = () => {
    switch (mode) {
      case InvitationMode.ACCEPT:
        return (
          <>
            {isDarkTheme ? (
              <FullLightLogo className={styles.logo} testId="gromaLogo" />
            ) : (
              <FullMediumLogo className={styles.logo} testId="gromaLogo" />
            )}
            <div className={styles.contentContainer}>
              <h1 className={styles.title}>
                {mode === InvitationMode.ACCEPT ? (
                  <FormattedMessage
                    id="invitations.acceptTitle"
                    defaultMessage="Finalize your account"
                    description="Accept Invite title"
                  />
                ) : (
                  <FormattedMessage
                    id="invitations.title"
                    defaultMessage="Invitations"
                    description="Invitations title"
                  />
                )}
              </h1>
              <Suspense fallback={<ComponentFactory.Loading />}>
                <AcceptInvitation email={email} token={token} />
              </Suspense>
            </div>
            <div className={styles.promptContainer}>
              <span className={styles.prompt}>
                <FormattedMessage
                  id="invitations.prompt"
                  defaultMessage={'Already have an account?\u2000{link}'}
                  description="Invitations prompt message"
                  values={{
                    link: (
                      <Link
                        href={buildUrlWithParams(AppRoutes.SignIn)}
                        variant={LinkVariants.SUB_NAV}
                        className={styles.promptLink}
                        testId="promptLink">
                        <FormattedMessage
                          id="invitations.promptLink"
                          defaultMessage="Sign In"
                          description="Invitations prompt link text"
                        />
                      </Link>
                    ),
                  }}
                />
              </span>
            </div>
          </>
        );
      default:
        // TODO: Invitations can be sent from users
        return (
          <>
            <h1>Invitations</h1>
          </>
        );
    }
  };

  return shouldShowLoading ? (
    <ComponentFactory.Loading isPage />
  ) : (
    <>
      {seoContent()}
      <main className={styles.mainContainer}>
        <div className={styles.mainWrapper}>{content()}</div>
      </main>
    </>
  );
});

export default Invitations;
